import { getAuthHeaders } from './authToken';

// Trailing slash on REACT_APP_API_URL would otherwise produce //api/api-keys
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

// Delegates to the shared helper so every call carries the signed-in user's Zitadel access token.
const getHeaders = (includeContentType = true) => getAuthHeaders(includeContentType);

const ensureOk = async (response, fallbackMessage) => {
  if (response.ok) return;

  const body = await response.json().catch(() => null);
  if (response.status === 409) {
    throw new Error(body?.message || body?.title || 'That key is already in use');
  }

  throw new Error(body?.message || body?.title || fallbackMessage);
};

/**
 * Get all API keys
 * @returns {Promise<Array<{id: number, name: string, key: string, phone: string|null, email: string|null, description: string|null, isActive: boolean, createdAt: string, updatedAt: string|null, lastUsedAt: string|null}>>}
 */
export const getApiKeys = async () => {
  const response = await fetch(`${API_BASE}/api/api-keys`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  await ensureOk(response, 'Failed to fetch API keys');

  return response.json();
};

/**
 * Get a single API key by ID
 * @param {number} id - API key ID
 */
export const getApiKeyById = async (id) => {
  const response = await fetch(`${API_BASE}/api/api-keys/${id}`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  await ensureOk(response, 'Failed to fetch API key');

  return response.json();
};

/**
 * Ask for an unused key without saving anything — the API checks the value isn't
 * taken and hands it back. Nothing is reserved in the meantime, so it can still be
 * claimed before the create lands; POST answers 409 then, same as a typed duplicate.
 * @returns {Promise<{key: string}>}
 */
export const generateApiKey = async () => {
  const response = await fetch(`${API_BASE}/api/api-keys/generate`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  await ensureOk(response, 'Failed to generate a key');

  return response.json();
};

/**
 * Create an API key. Leave `key` out and the API generates one (XXXXX-XXXXX-XXXXX);
 * supply it to pin a specific value.
 * @param {Object} data
 * @param {string} data.name - Required
 * @param {string} [data.phone]
 * @param {string} [data.email]
 * @param {string} [data.description]
 * @param {boolean} [data.isActive]
 * @param {string} [data.key]
 */
export const createApiKey = async (data) => {
  const payload = {
    name: data.name,
    phone: data.phone,
    email: data.email,
    description: data.description,
    isActive: data.isActive,
  };

  if (data.key) {
    payload.key = data.key;
  }

  const response = await fetch(`${API_BASE}/api/api-keys`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });

  await ensureOk(response, 'Failed to create API key');

  return response.json();
};

/**
 * Update an API key. The PUT replaces the whole row: any field left out is nulled,
 * and a missing isActive re-enables a disabled key. Always send all five fields.
 *
 * `key` is the one exception — omitted or null means "keep", an empty string is a 400.
 * So it goes out only when supplied. Re-sending the row's current value is a no-op
 * rather than a 409; any other value replaces the key immediately.
 * @param {number} id - API key ID
 * @param {{name: string, phone: string, email: string, description: string, isActive: boolean, key?: string}} data
 */
export const updateApiKey = async (id, data) => {
  const payload = {
    name: data.name,
    phone: data.phone,
    email: data.email,
    description: data.description,
    isActive: data.isActive,
  };

  if (data.key) {
    payload.key = data.key;
  }

  const response = await fetch(`${API_BASE}/api/api-keys/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });

  await ensureOk(response, 'Failed to update API key');

  return response.json();
};

/**
 * Roll the key value. Takes effect immediately and cannot be undone — the previous
 * key stops working as soon as this returns.
 * @param {number} id - API key ID
 */
export const regenerateApiKey = async (id) => {
  const response = await fetch(`${API_BASE}/api/api-keys/${id}/regenerate`, {
    method: 'POST',
    headers: getHeaders(false),
  });

  await ensureOk(response, 'Failed to regenerate API key');

  return response.json();
};
